<template>
  <div>
    <section class="page-hero" :style="{ '--ph-img': `url(${images.hero2})` }">
      <div class="wrap">
        <p class="eyebrow reveal">What we do</p>
        <h1 class="reveal">Designed for life</h1>
        <p class="reveal">A complete home journey — from discovery to everyday living.</p>
        <p class="crumb reveal"><router-link to="/">Home</router-link> · Services</p>
      </div>
    </section>

    <!-- Process -->
    <section class="section tint">
      <div class="wrap">
        <div class="section-head reveal">
          <p class="eyebrow">How we work</p>
          <h2>Take the whole journey, or just the part you need</h2>
          <p>Choose from our delicated services which are fully customizable to your needs!</p>
        </div>
        <div class="pkg-tabs reveal">
          <button
            v-for="t in tabs"
            :key="t.id"
            class="pkg-tab"
            :class="{ active: activeTab === t.id }"
            @click="activeTab = t.id"
          >
            {{ t.label }}
          </button>
        </div>
        <p v-if="currentTab.tagline" class="pkg-tagline">{{ currentTab.tagline }}</p>
        <div v-if="currentTab.includes">
          <p v-if="currentTab.blurb" class="pkg-sub">{{ currentTab.blurb }}</p>
          <div class="cards pkg-grid">
            <div v-for="item in currentTab.includes" :key="item.no" class="card pkg">
              <span class="card-num">{{ item.no }}</span>
              <h3>{{ item.title }}</h3>
              <p>{{ item.text }}</p>
            </div>
          </div>
        </div>
        <div v-else-if="currentTab.blurb" class="pkg-pending">
          <h3>{{ currentTab.label }}</h3>
          <p>{{ currentTab.blurb }}</p>
        </div>
        <div v-else class="cards pkg-grid">
          <div v-for="p in activePackages" :key="p.no" class="card pkg">
            <span class="card-num">{{ p.no }}</span>
            <h3>{{ p.title }}</h3>
            <p>{{ p.deliverables }}</p>
            <p v-if="p.idealFor" class="pkg-for">
              <span>Ideal for</span>{{ p.idealFor }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="section" style="text-align: center">
      <div class="wrap reveal">
        <p class="eyebrow">Have a project in mind?</p>
        <h2 style="font-size: clamp(2rem, 4vw, 3.2rem); margin: 1rem 0 1.4rem">Every space we craft starts with a plan.</h2>
        <router-link to="/contact" class="btn btn-primary">Schedule a date</router-link>
      </div>
    </section>
  </div>
</template>

<script>
import { images } from "@/data/projects";

export default {
  name: "ServicesView",
  data() {
    return {
      images,
      activeTab: "lifestyle",
      // Each tab is a bundle built from the numbered points below.
      tabs: [
        {
          id: "lifestyle",
          label: "Lifestyle Blueprint™",
          tagline: "Design strategy before renovation.",
          points: [1, 2, 3, 4],
        },
        {
          id: "signature",
          label: "EIKA Signature Design™",
          tagline: "Complete design package.",
          points: [1, 2, 3, 4, 5, 6],
        },
        {
          id: "concierge",
          label: "EIKA Project Concierge™",
          tagline: "End-to-end project management.",
          points: [7, 8],
        },
        {
          id: "movein",
          label: "Move-In Ready™",
          tagline: "Design, renovation, furniture, styling and handover.",
          points: [1, 2, 3, 4, 5, 6, 7, 8, 9],
        },
        {
          id: "oncall",
          label: "Designer On Call™",
          tagline: "Monthly consultation for homeowners or businesses.",
          blurb: "A designer in your corner, month after month. Regular consultations for homeowners and businesses — fresh ideas, practical advice and a trained eye whenever your space needs it.",
        },
        {
          id: "home",
          label: "EIKA Home Concierge™",
          tagline: "Ongoing guidance and support to help you care for your home long after the renovation is complete.",
          includes: [
            {
              no: "01",
              title: "Digital Homeowner Handbook",
              text: "A complete digital guide to every material, fitting and finish in your home.",
            },
            {
              no: "02",
              title: "Maintenance Schedules",
              text: "Tailored upkeep schedules for the different materials in your home.",
            },
            {
              no: "03",
              title: "Cleaning Instructions",
              text: "The right way to care for quartz, sintered stone, wood, microcement and more.",
            },
            {
              no: "04",
              title: "Warranty Tracker",
              text: "One place to track warranties for your appliances and contractors.",
            },
            {
              no: "05",
              title: "Defect Reporting Guide",
              text: "What to document and who to call when something isn't right.",
            },
            {
              no: "06",
              title: "Recommended Maintenance Vendors",
              text: "Our trusted list of vendors for servicing and repairs.",
            },
            {
              no: "07",
              title: "Annual Home Health Check",
              text: "A yearly inspection of your home's condition. Optional paid add-on.",
            },
            {
              no: "08",
              title: "Seasonal Reminders",
              text: "Timely nudges for aircon servicing, sealant inspection and water heater maintenance.",
            },
            {
              no: "09",
              title: "Priority Support",
              text: "First in line for future alterations and improvement works.",
            },
            {
              no: "10",
              title: "Monthly Consultation",
              text: "Lifestyle or operations update consultations for homeowners and businesses.",
            },
          ],
        },
      ],
      // Packages are modular: each can be taken on its own, or combined.
      packages: [
        {
          no: "01",
          title: "First Consultation",
          deliverables: "Lifestyle questionnaire, site visit, measurements, budget strategy, feasibility report.",
          idealFor: "Clients deciding whether to renovate",
        },
        {
          no: "02",
          title: "Concept Design",
          deliverables: "Mood board, material direction, colour palette, furniture concept, layout options.",
          idealFor: "DIY clients or early planning",
        },
        {
          no: "03",
          title: "Space Planning",
          deliverables: "Optimised floor plans, furniture layouts, storage planning, circulation study.",
          idealFor: "BTO & resale owners",
        },
        {
          no: "04",
          title: "3D Visualisation",
          deliverables: "Photorealistic renders, walkthrough images, lighting studies.",
          idealFor: "Clients wanting design confidence",
        },
        {
          no: "05",
          title: "Renovation Blueprint",
          deliverables: "Full construction drawings, elevations, electrical, lighting and carpentry drawings.",
          idealFor: "Contractors or self-managed projects",
        },
        {
          no: "06",
          title: "Material & Finishes Package",
          deliverables: "Tile selections, laminates, paint colours, stone, sanitaryware schedule.",
          idealFor: "Premium homes",
        },
        {
          no: "07",
          title: "Project Management",
          deliverables: "Site coordination, timeline management, defect inspections, contractor liaison.",
          idealFor: "Clients who don't have time",
        },
        {
          no: "08",
          title: "Procurement Service",
          deliverables: "Supplier quotations, ordering, delivery coordination, quality checks.",
          idealFor: "Busy professionals",
        },
        {
          no: "09",
          title: "Furniture & Styling",
          deliverables: "Furniture sourcing, shopping list, styling, artwork, accessories.",
          idealFor: "Clients furnishing completed homes",
        },
      ],
    };
  },
  computed: {
    currentTab() {
      return this.tabs.find((t) => t.id === this.activeTab);
    },
    activePackages() {
      const points = this.currentTab.points || [];
      return this.packages.filter((p) => points.includes(Number(p.no)));
    },
  },
};
</script>
